/**
 * Event answers as metadata sends them. The schemas are generated from metadata's openapi.yaml
 * (`npm run generate`), and the types come from the schemas.
 *
 * The types from EventSeverity on are not wire shapes.
 */
import { z } from 'zod';

import type { Rect as RectSchema } from './generated/metadata.zod';
import {
    EmbeddingHit as EmbeddingHitSchema,
    EventMetadata as EventMetadataSchema,
    EventSearchResult as EventSearchResultSchema,
} from './generated/metadata.zod';
import type { LoadState } from './snapshot';

export type Rect = z.infer<typeof RectSchema>;

/**
 * A row of an `/events/search` answer. The query picks the mode: a similarity search scores
 * every row, a page in recency order scores none.
 */
export { EventSearchResultSchema };
export type EventSearchResult = z.infer<typeof EventSearchResultSchema>;

/**
 * A row as the app holds it: the server's row, and the verdict of an enroll made in the app
 * until the server's row replaces it.
 */
export interface EventRow extends EventSearchResult {
    /** The operator declared this sighting NOT to be `enrollmentId`. */
    isEnrollmentNegative?: boolean;
}

/** A row of a vector search, which always scores it. */
export const EventMetadataWithSimilaritySchema = EventMetadataSchema.extend({
    similarity: z.number(),
});

/**
 * One vector-search hit off the embedding tables, with no `events` JOIN: what the lean search
 * answers with, and all a scene-embedding row ever has.
 */
export { EmbeddingHitSchema };
export type EmbeddingHit = z.infer<typeof EmbeddingHitSchema>;

/**
 * The levels of the rating the scene_insight agent stamps on an
 * `event.sceneDescription`, lowest first. The event's `severity` attribute
 * carries the rank (0 to 3); these names are the vocabulary of the feed's
 * minimum-severity filter and the rule editor's threshold. A frozen object
 * plus the union it derives, rather than an enum, because the persisted filter
 * arrives as a string and an enum member is not assignable from one without a
 * cast at every entry point.
 */
export const EventSeverity = {
    Lowest: 'lowest',
    Low: 'low',
    Normal: 'normal',
    High: 'high',
} as const;

export type EventSeverity = (typeof EventSeverity)[keyof typeof EventSeverity];

/**
 * The scope the AI chat's open_events_panel tool asks the events feed to show: every filter at
 * once, null or '' for unfiltered, and a nonce that makes it apply exactly once.
 */
export interface EventSearchRequest {
    cameraId: string | null;
    query: string;
    startMs: number | null;
    endMs: number | null;
    /** An event.* category; null for every event. */
    category: string | null;
    minSeverity: EventSeverity;
    nonce: string;
}

/** A read of /events/search: its query string, which a feed cuts older pages from. */
export interface EventSearchQuery {
    params: string;
}

/**
 * The events that one query found so far. A ranked query has one page; a newest-first one grows
 * by older pages while `hasOlder`.
 */
export interface EventSearchSnapshot extends LoadState {
    events: EventRow[];
    /** True after the first page landed. */
    isLoaded: boolean;
    hasOlder: boolean;
    /** The gate takes the latest rows when this changes. */
    olderPageCount: number;
}

/** A read of /embeddings/search, by its query string. */
export interface SceneSearchQuery {
    params: string;
}

/** The embedding hits that one query found, best first. */
export interface SceneSearchSnapshot extends LoadState {
    hits: EmbeddingHit[];
}

/** A run of ~1s-apart frames coalesced into the one moment they describe. */
export interface SceneMoment extends EmbeddingHit {
    clusterStart: number;
    clusterEnd: number;
    clusterCount: number;
    durationMs: number;
}

/**
 * Not a wire shape: how one event card lays out. EventCardList's container
 * query picks it from the measured width, and a standalone card overrides it.
 */
export type EventCardVariant = 'line' | 'tile';

/** One row of an event card's info table. Never rule IDs. */
export interface EventCardInfoRow {
    label: string;
    /** An event attribute as the wire carried it; a JSON null is a missing one. */
    value: string | number | null;
}

/**
 * A count the metadata service answered with. The service serves rows rather than counts, so a
 * count is the length of one page: `isCapped` says the page filled up and the count is a floor.
 */
export interface ActivityCount {
    count: number;
    isCapped: boolean;
    /** The rows over time, oldest bin first: the shape of the day the tile draws. */
    bins: number[];
    /**
     * The time the bins cover, up to the moment of the search. A capped page ends at its oldest
     * row, so its bins cover less than the day.
     */
    spanMs: number;
}

/** How much the organization saw in the last day, as the home launcher's tiles report it. */
export interface RecentActivitySnapshot {
    objects: ActivityCount;
    events: ActivityCount;
    isLoading: boolean;
    error: Error | null;
}
